import React from 'react';
import { CheckCircle2Icon, FilesIcon, RefreshCwIcon } from 'lucide-react';
import { DocumentList } from './DocumentList';
import { IntegrationErrorCard } from './IntegrationErrorCard';
import { PackageStatusPill } from './ui/StatusPill';
import type { BorrowerPair, DisclosureDocument, IntegrationError, PackageState } from '../types/disclosures';
import { pluralize } from '../utils/format';
interface ReviewPackageStepProps {
  pair: BorrowerPair;
  packageState: PackageState;
  error: IntegrationError | null;
  onSend: () => void;
  onRegenerate: () => void;
  onToggleDocument: (documentId: string) => void;
  onPreview: (document: DisclosureDocument) => void;
  onErrorAction: (error: IntegrationError) => void;
}
export function ReviewPackageStep({
  pair,
  packageState,
  error,
  onSend,
  onRegenerate,
  onToggleDocument,
  onPreview,
  onErrorAction
}: ReviewPackageStepProps) {
  const selected = packageState.documents.filter(doc => packageState.selectedDocumentIds.includes(doc.id));
  const pages = selected.reduce((total, doc) => total + doc.pages, 0);
  const isSent = packageState.status === 'sent';
  const sentRecipients = pair.recipients.filter(recipient => packageState.recipientIds.includes(recipient.id));
  return <div className="space-y-4" data-id="element-265" data-mp="16ax80a23rl6n">
      <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-card" data-id="element-266" data-mp="1b405uudwxgsn">
        <h2 className="text-[16px] font-semibold tracking-[-0.01em] text-slate-900" data-id="element-267" data-mp="1f5bvv10p841d">
          {isSent ? 'Initial Disclosures Sent' : 'Review Disclosure Package'}
        </h2>
        <p className="mt-1 text-[13px] leading-5 text-slate-500" data-id="element-268" data-mp="1k5zmvmoxgkvl">
          {isSent ? `${pair.label.replace(' + ', ' and ')} have been sent their disclosure package.` : 'Review the documents that were generated before sending them to the borrower.'}
        </p>

        <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50/60 p-4" data-id="element-269" data-mp="3qwh0l19wqohg">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between" data-id="element-270" data-mp="3vu8yqd15b6u3">
            <div className="flex items-start gap-3" data-id="element-271" data-mp="3ntjhab87ot1w">
              <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${isSent ? 'bg-emerald-100 text-emerald-700' : 'bg-white text-slate-500 ring-1 ring-slate-200'}`} data-id="element-272" data-mp="3iu6nf6ixalpt">
                {isSent ? <CheckCircle2Icon className="h-5 w-5" aria-hidden="true" data-id="element-273" data-mp="am80lajoll0x" /> : <FilesIcon className="h-5 w-5" aria-hidden="true" data-id="element-274" data-mp="5nocb3hm7x5e" />}
              </span>
              <div data-id="element-275" data-mp="kc18o1km7i0f">
                <div className="flex items-center gap-2" data-id="element-276" data-mp="kb9e8u2r7qbq">
                  <p className="text-[14px] font-semibold text-slate-900" data-id="element-277" data-mp="fd7wqdkz7z2d">
                    Initial Disclosure Package
                  </p>
                  <PackageStatusPill status={packageState.status} data-id="element-278" data-mp="te0st2qxf27l" />
                </div>
                <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[12.5px] text-slate-500" data-id="element-279" data-mp="pc6xl9zi2qet">
                  <span data-id="element-280" data-mp="180ziota4xok7">{pluralize(selected.length, 'Document')}</span>
                  <span aria-hidden="true" className="text-slate-300" data-id="element-281" data-mp="1czatb689833h">
                    •
                  </span>
                  <span data-id="element-282" data-mp="yxycksl9rxgj">{pluralize(pages, 'Page')}</span>
                  <span aria-hidden="true" className="text-slate-300" data-id="element-283" data-mp="z1cigat56osq">
                    •
                  </span>
                  <span data-id="element-284" data-mp="p9gdqgwjw0fj">{pair.label}</span>
                </p>
              </div>
            </div>

            {isSent ? <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-[12.5px] sm:text-right" data-id="element-285" data-mp="u0ygnai4wb35">
                <dt className="text-slate-500" data-id="element-286" data-mp="g9bbsma4lu8r">Sent</dt>
                <dd className="font-medium text-slate-900" data-id="element-287" data-mp="g6p32jbi18oy">{packageState.sentAt}</dd>
                <dt className="text-slate-500" data-id="element-288" data-mp="k80umudxo3zs">Delivery</dt>
                <dd className="font-medium text-slate-900" data-id="element-289" data-mp="6bdktr0vtjcg">
                  {packageState.delivery === 'paper' ? 'Paper / Fulfillment' : 'Electronic'}
                </dd>
                <dt className="text-slate-500" data-id="element-290" data-mp="1dmley3fl664h">Recipients</dt>
                <dd className="font-medium text-slate-900" data-id="element-291" data-mp="1doxuk9xvg4ed">{sentRecipients.length}</dd>
              </dl> : <div className="flex shrink-0 items-center gap-2" data-id="element-292" data-mp="14ujg29l60xwb">
                <button type="button" onClick={onRegenerate} className="inline-flex items-center gap-1.5 rounded-md bg-white px-3 py-2 text-[13px] font-medium text-slate-700 ring-1 ring-slate-200 transition hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" data-id="element-293" data-mp="14soviwdt37sr">
                  <RefreshCwIcon className="h-3.5 w-3.5" data-id="element-294" data-mp="luvuy0qnn9yj" />
                  Regenerate
                </button>
                <button type="button" onClick={onSend} className="rounded-md bg-slate-900 px-4 py-2.5 text-[13px] font-semibold text-white transition hover:bg-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2" data-id="element-295" data-mp="gxvwoc01rbhz">
                  Send Disclosures
                </button>
              </div>}
          </div>
        </div>
      </section>

      {error && <IntegrationErrorCard error={error} onAction={() => onErrorAction(error)} data-id="element-296" data-mp="2sjo3d0owts7l" />}

      <DocumentList documents={packageState.documents} selectedIds={packageState.selectedDocumentIds} onToggle={onToggleDocument} onPreview={onPreview} readOnly={isSent} data-id="element-297" data-mp="2xjiriwhaw3oy" />
    </div>;
}